import moment from "moment";
import { ProgramPaymentType, ProgramScheduleType } from "../../../types/dto/program";
import { CopyProgramSchedulePaymentStudentType } from "../../../types/dto/scheduleStudent";

export function formatScheduleMonth(
  month: Date | string,
  fallbackDate?: Date | string,
  pattern = "MMM / YYYY"
): string {
  const monthMoment = moment(month);

  if (monthMoment.isValid()) {
    return monthMoment.format(pattern);
  }

  const fallbackMoment = moment(fallbackDate);
  if (fallbackMoment.isValid()) {
    return fallbackMoment.format(pattern);
  }

  return "—";
}

export function resolveScheduleMonthDate(
  month: Date | string,
  fallbackDate?: Date | string
): moment.Moment {
  const monthMoment = moment(month);

  if (monthMoment.isValid()) {
    return monthMoment.startOf("month");
  }

  const fallbackMoment = moment(fallbackDate);
  if (fallbackMoment.isValid()) {
    return fallbackMoment.startOf("month");
  }

  return moment.invalid();
}

function resolveStudentScheduleMonth(
  schedule: CopyProgramSchedulePaymentStudentType
): moment.Moment {
  return resolveScheduleMonthDate(
    schedule.month as string | Date,
    (schedule.payment_due_date ||
      schedule.payment_schedule?.day_of_month_payment_limit) as
      | string
      | Date
      | undefined
  );
}

/** Último mes de cuota pagado (por cronograma), o null si no hay pagos de cuota. */
export function getLastPaidScheduleMonth(
  payments: ProgramPaymentType[]
): moment.Moment | null {
  let lastMonth: moment.Moment | null = null;

  for (const payment of payments) {
    if (!payment.program_payment_schedule_id || !payment.program_schedule) {
      continue;
    }

    const scheduleMonth = resolveScheduleMonthDate(
      payment.program_schedule.month,
      payment.program_schedule.day_of_month_payment_limit
    );

    if (!scheduleMonth.isValid()) continue;

    if (!lastMonth || scheduleMonth.isAfter(lastMonth, "month")) {
      lastMonth = scheduleMonth;
    }
  }

  return lastMonth;
}

/**
 * Cronogramas válidos desde el último pago en adelante (incluye el mes del último pago).
 * Si no hay pagos previos, devuelve todos los meses válidos ordenados.
 * Omite fechas inválidas.
 */
export function filterSchedulesFromLastPayment(
  schedules: ProgramScheduleType[],
  payments: ProgramPaymentType[]
): ProgramScheduleType[] {
  const lastPaidMonth = getLastPaidScheduleMonth(payments);

  return schedules
    .map((schedule) => ({
      schedule,
      month: resolveScheduleMonthDate(
        schedule.month,
        schedule.day_of_month_payment_limit
      ),
    }))
    .filter(({ month }) => {
      if (!month.isValid()) return false;
      if (!lastPaidMonth) return true;
      return month.isSameOrAfter(lastPaidMonth, "month");
    })
    .sort((a, b) => a.month.valueOf() - b.month.valueOf())
    .map(({ schedule }) => schedule);
}

/**
 * Cuotas activas asignadas al estudiante que aún no están pagadas.
 * Incluye meses vencidos (pago extraordinario) y pagos parciales.
 * Omite cuotas ya pagadas y fechas inválidas.
 */
export function filterAssignedSchedulesFromLastPayment(
  assignedSchedules: CopyProgramSchedulePaymentStudentType[]
): CopyProgramSchedulePaymentStudentType[] {
  return assignedSchedules
    .filter((schedule) => schedule.status === "active")
    .filter((schedule) => schedule.payment_status !== "paid")
    .map((schedule) => ({
      schedule,
      month: resolveStudentScheduleMonth(schedule),
    }))
    .filter(({ month }) => month.isValid())
    .sort((a, b) => a.month.valueOf() - b.month.valueOf())
    .map(({ schedule }) => schedule);
}
